import type { FC, ReactElement } from 'react';
import { createElement } from 'react';
import { useEffect, useState } from 'react';

import ComplaintForm from '@tmh-website/components/shared/StaticForms/ComplaintForm';
import ContactFormCHDE from '@tmh-website/components/shared/StaticForms/ContactFormCH_DE';
import ContactFormEN from '@tmh-website/components/shared/StaticForms/ContactFormEN';
import ETContactForm from '@tmh-website/components/shared/StaticForms/ETContactForm';
import HubSpotFormRenderer from '@tmh-website/components/shared/HubSpotFormRenderer';
import BusinessUnitChargingWeb2CaseForm from '@tmh-website/components/shared/StaticForms/BusinessUnitChargingWeb2CaseForm';
import BusinessUnitSolutionsWeb2CaseForm from '@tmh-website/components/shared/StaticForms/BusinessUnitSolutionsWeb2CaseForm';
import PaymentForm from '@tmh-website/components/shared/StaticForms/PaymentForm';
import ProductSupportForm from '@tmh-website/components/shared/StaticForms/ProductSupportForm';
import ProductSupportFormUSAEN from '@tmh-website/components/shared/StaticForms/ProductSupportFormUSA_EN';
import ShippingForm from '@tmh-website/components/shared/StaticForms/ShippingForm';
import StyledGlobal, { getStyledSectionBaseProps, type SectionBaseProps } from '@tmh-website/styles/global';
import Web2CaseForm from '@tmh-website/components/shared/StaticForms/Web2CaseForm';
import SupportForEvDriversWeb2CaseForm from '@tmh-website/components/shared/StaticForms/SupportForEvDriversWeb2CaseForm';
import WhitepaperForm from '@tmh-website/components/global/articles/WhitepaperForm';
import { ButtonHoverThemeColor, ButtonThemeColor } from '@tmh-website/ui/Button';
import { Color } from '@tmh-website/shared/colors';
import { extractLocalePrefixFromUrl, isLocalePrefix } from '@tmh-website/services/utils.service';
import { forceSalAnimations } from '@tmh-website/services/sal.service';
import { FormElementThemeColor } from '@tmh-website/ui/FormElements';
import { LocalePrefix } from '@tmh-website/shared/const';
import { stringToHtmlID } from '@tmh-website/services/page-navigation.service';
import {
  FormParameters,
  FormVariant,
  StaticFormVariant,
  FORM_VARIANT_TO_TYPE_GUARD_MAP,
  isStaticFormParameters,
} from '@tmh-website/services/form.service';

import type { DynamicFormVariant, FormAlignment, FormComponent } from './Form.types';
import * as Styled from './Form.styles';
import { DEFAULT_FORM_ALIGNMENT } from './Form.config';
import { formThemes } from './Form.theme';
import { isLocaleSupportedByFormVariant } from './Form.helpers';

export interface FormComponentBaseProps {
  formParameters: FormParameters;
  formElementThemeColor: FormElementThemeColor;
  buttonThemeColor: ButtonThemeColor;
  buttonHoverThemeColor: ButtonHoverThemeColor;
  locale: LocalePrefix;
}

interface FormProps extends SectionBaseProps {
  data: FormComponent;
  locale?: LocalePrefix;
  currentPath?: string;
}

const Form = ({ data, locale, currentPath, smallPaddingTop }: FormProps) => {
  const [_locale, _setLocale] = useState<LocalePrefix | undefined>(locale);

  const alignment: FormAlignment = data.alignment ?? DEFAULT_FORM_ALIGNMENT;
  const themeColor = data.backgroundColor ?? Color.Neutral_0_White;

  useEffect(() => {
    _setLocale(extractLocalePrefixFromUrl(new URL(window.location.href)));
  }, []);

  useEffect(forceSalAnimations, [_locale]);

  if (!isLocalePrefix(_locale)) {
    return null;
  }

  let formElement: ReactElement | null = null;

  for (const variant of Object.values(FormVariant)) {
    const guard = FORM_VARIANT_TO_TYPE_GUARD_MAP[variant];

    if (guard(data.formParameters as FormParameters)) {
      const formParameters: FormParameters = data.formParameters as FormParameters;

      const formVariant: DynamicFormVariant | StaticFormVariant = isStaticFormParameters(formParameters)
        ? formParameters.params.form
        : formParameters.type;

      const staticFormVariant: StaticFormVariant | undefined = isStaticFormParameters(formParameters)
        ? formParameters.params.form
        : undefined;

      if (isLocaleSupportedByFormVariant(_locale, formVariant)) {
        formElement = createElement(FORM_VARIANT_TO_COMPONENT_MAP[formVariant], {
          formParameters,
          formVariant: staticFormVariant,
          ...formThemes[themeColor],
          locale: _locale,
          currentPath,
        });
      }

      break;
    }
  }

  if (formElement === null) {
    return null;
  }

  return (
    <Styled.Section
      {...getStyledSectionBaseProps(data, smallPaddingTop)}
      id={data.pageNavigationCustomId ?? stringToHtmlID(data.pageNavigationLabel)}
      // @ts-ignore
      $themeColor={themeColor}
      data-sal='slide-in-container'>
      <StyledGlobal.Wrapper>
        {/* Headline */}
        <Styled.AdjustHeadlineTextBlock
          teaser={data.teaser}
          headline={data.headline}
          text={data.text}
          textColor={formThemes[themeColor].text}
          data-sal-child
          data-sal-delay={0}
        />
        {/* END | Headline */}

        {/* Form */}
        <Styled.FormWrapper $alignment={alignment}>
          <StyledGlobal.WrapperSmall>{formElement}</StyledGlobal.WrapperSmall>
        </Styled.FormWrapper>
        {/* END | Form */}
      </StyledGlobal.Wrapper>
    </Styled.Section>
  );
};

export default Form;

// This map should be placed in the `Form.config.ts` file, but for some reason doing so generates a following error:
//
//       throw new Error(`Could not load js config file ${file}: ${error.message}`);
//             ^
// Error: Could not load js config file /tmh-website/apps/cms/dist/apps/cms/config/cron-tasks.js: Cannot find module './assets/images/flag-ch.svg'
// Require stack:
// - /tmh-website/apps/cms/dist/libs/components/src/assets.js
// - /tmh-website/apps/cms/dist/libs/components/src/lib/global/Header/Header.styles.js
// - /tmh-website/apps/cms/dist/libs/shared/src/hooks/useLogoComponent/useLogoComponent.config.js
// - /tmh-website/apps/cms/dist/libs/shared/src/hooks/useLogoComponent/useLogoComponent.helpers.js
// - /tmh-website/apps/cms/dist/libs/shared/src/hooks/useLogoComponent/useLogoComponent.js
// - /tmh-website/apps/cms/dist/libs/shared/src/hooks/useLogoComponent/index.js
// - /tmh-website/apps/cms/dist/libs/shared/src/hooks/index.js
// - /tmh-website/apps/cms/dist/libs/ui/src/lib/DropdownMenu/DropdownMenu.js
// - /tmh-website/apps/cms/dist/libs/ui/src/lib/DropdownMenu/index.js
// - /tmh-website/apps/cms/dist/libs/ui/src/lib/Select/Select.js
// - /tmh-website/apps/cms/dist/libs/ui/src/lib/Select/index.js
// - /tmh-website/apps/cms/dist/libs/components/src/lib/shared/StaticForms/BusinessUnitChargingWeb2CaseForm/BusinessUnitChargingWeb2CaseForm.js
// - /tmh-website/apps/cms/dist/libs/components/src/lib/blocks/Form/Form.config.js
// - /tmh-website/apps/cms/dist/libs/components/src/blocks-config.js
// - /tmh-website/apps/cms/dist/libs/components/src/blocks-populate-options.js
// - /tmh-website/apps/cms/dist/apps/cms/src/api/shared/services/page.js
// - /tmh-website/apps/cms/dist/apps/cms/config/cron-tasks.js
// ...
const FORM_VARIANT_TO_COMPONENT_MAP: Record<DynamicFormVariant | StaticFormVariant, FC<any>> = {
  [FormVariant.Hubspot]: HubSpotFormRenderer,
  [FormVariant.Mailchimp]: WhitepaperForm,
  [StaticFormVariant.BusinessUnitChargingWeb2Case]: BusinessUnitChargingWeb2CaseForm,
  [StaticFormVariant.BusinessUnitSolutionsWeb2Case]: BusinessUnitSolutionsWeb2CaseForm,
  [StaticFormVariant.Complaint]: ComplaintForm,
  [StaticFormVariant.ContactCH_DE]: ContactFormCHDE,
  [StaticFormVariant.ContactEN]: ContactFormEN,
  [StaticFormVariant.ETContact]: ETContactForm,
  [StaticFormVariant.ETWeb2CaseContact]: Web2CaseForm,
  [StaticFormVariant.ETWeb2CaseSupport]: Web2CaseForm,
  [StaticFormVariant.Payment]: PaymentForm,
  [StaticFormVariant.ProductSupport]: ProductSupportForm,
  [StaticFormVariant.ProductSupportUSA_EN]: ProductSupportFormUSAEN,
  [StaticFormVariant.Shipping]: ShippingForm,
  [StaticFormVariant.SupportForEvDriversWeb2Case]: SupportForEvDriversWeb2CaseForm,
  [StaticFormVariant.Web2Case]: Web2CaseForm,
};
